"use client";

/**
 * SWR provider — systemic replacement for the per-page useState+useEffect+fetch
 * pattern + the sessionStorage stop-gap cache in lib/dataCache.js.
 *
 * The fetcher is fetchWithAuth (Firebase-token-aware, with retry + timeout +
 * slow-request warnings). SWR gets:
 *   - automatic cache with a 60s dedupe window
 *   - revalidate on focus (fixes "stale data after tab switch")
 *   - retry on transient errors (in addition to what fetchWithAuth already does)
 *   - shared cache across route changes → no more "blank card on navigation"
 *
 * Error handling: if fetchWithAuth returns a non-ok response, the fetcher
 * throws an Error with `.status` and `.info` so pages can render a useful
 * message instead of just "something went wrong".
 */

import { SWRConfig } from "swr";
import fetchWithAuth from "@/hooks/fetchWithAuth";

async function swrFetcher(url) {
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const err = new Error(`Request failed: ${res.status} ${res.statusText}`);
    err.status = res.status;
    try {
      err.info = await res.json();
    } catch {
      // body not JSON — keep status only
    }
    throw err;
  }
  return res.json();
}

export default function SWRProvider({ children }) {
  return (
    <SWRConfig
      value={{
        fetcher: swrFetcher,
        // Dedupe identical in-flight requests for 60s (prevents the "every
        // component on a page re-fetches the same URL" class of bug).
        dedupingInterval: 60_000,
        // Refresh when the user refocuses the tab — fixes stale data after
        // long idle periods without forcing a full page reload.
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
        // fetchWithAuth already handles 502/503/504 retries; SWR only retries
        // on thrown errors (network-level). Cap the noise at 2.
        errorRetryCount: 2,
      }}
    >
      {children}
    </SWRConfig>
  );
}
